import React, { useState } from 'react'
import { RELATION_COPY } from 'features/taskLinks/linkCopy'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { Button, CircularProgress, TextField, MenuItem, IconButton } from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import AssignmentIcon from '@mui/icons-material/Assignment'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { closeWidget } from 'ducks/guruWidget/actions'
import { PRIORITY_META, priorityMeta } from 'features/taskManagement/taskPriority'

const toDatetimeLocal = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const SchemaField = ({ field, value, onChange, error }) => {
  const common = {
    fullWidth: true,
    size: 'small',
    margin: 'dense',
    label: field.label,
    error: !!error,
    helperText: error || '',
  }
  if (field.type === 'select') {
    return (
      <TextField {...common} select value={value ?? ''} onChange={(e) => onChange(field.key, e.target.value)}>
        {(field.options || []).map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
        ))}
      </TextField>
    )
  }
  if (field.type === 'textarea') {
    return <TextField {...common} multiline minRows={2} value={value ?? ''} onChange={(e) => onChange(field.key, e.target.value)} InputProps={{ readOnly: !!field.readOnly }} />
  }
  if (field.type === 'date') {
    return (
      <TextField
        {...common}
        type="datetime-local"
        value={toDatetimeLocal(value)}
        onChange={(e) => onChange(field.key, e.target.value ? new Date(e.target.value).toISOString() : '')}
        InputLabelProps={{ shrink: true }}
      />
    )
  }
  if (field.type === 'memberPicker' || field.type === 'filePicker') {
    return <TextField {...common} value={field.displayValue || value || ''} InputProps={{ readOnly: true }} />
  }
  return <TextField {...common} value={value ?? ''} onChange={(e) => onChange(field.key, e.target.value)} InputProps={{ readOnly: !!field.readOnly }} />
}

// Shared severity → chip colour. Mirrors the SEVERITY_TINT used elsewhere
// so the same word feels the same shade across the app.
const SEVERITY_COLOURS = {
  critical: { bg: '#fdecea', fg: '#b00020' },
  high:     { bg: '#ffedd5', fg: '#c2410c' },
  medium:   { bg: '#fef3c7', fg: '#d97706' },
  low:      { bg: '#dcfce7', fg: '#15803d' },
}


const INCIDENT_CATEGORY_LABELS = {
  fire: 'Fire',
  gas: 'Gas',
  electrical: 'Electrical',
  structural: 'Structural',
  water: 'Water / flooding',
  asbestos: 'Asbestos',
  security: 'Security',
  health: 'Health & safety',
  lift: 'Lift / elevator',
  other: 'Other',
}

const formatDate = (value) => {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

const SEVERITY_OPTIONS = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
]

const CATEGORY_OPTIONS = Object.entries(INCIDENT_CATEGORY_LABELS).map(([value, label]) => ({ value, label }))

const initIncidentDraft = (preview = {}) => ({
  severity: preview.severity || 'medium',
  category: preview.category || 'other',
  suggestedActions: (preview.suggestedActions || preview.suggested_actions || []).map((sa) => ({
    title: sa.title || '',
    severity: sa.severity || 'medium',
    description: sa.description || '',
    suggestedDueBy: sa.suggestedDueBy || sa.suggested_due_by || null,
  })),
})

const cleanRemedials = (list = []) =>
  list
    .map((sa) => ({
      title: (sa.title || '').trim(),
      severity: sa.severity || 'medium',
      description: (sa.description || '').trim() || null,
      suggestedDueBy: sa.suggestedDueBy || sa.suggested_due_by || null,
    }))
    .filter((sa) => sa.title !== '')

const draftToPreview = (draft, preview = {}) => ({
  ...preview,
  severity: draft?.severity ?? preview.severity,
  category: draft?.category ?? preview.category,
  suggestedActions: cleanRemedials(draft?.suggestedActions ?? preview.suggestedActions ?? preview.suggested_actions ?? []),
})

const toDateInput = (iso) => (iso ? String(iso).slice(0, 10) : '')

const Card = styled.div`
  background: ${({ theme }) => theme.colors.backgroundColor};
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-left: 4px solid ${({ $status }) =>
    $status === 'confirmed' ? '#4caf50' :
    $status === 'failed' ? '#f44336' : '#f57c00'};
  border-radius: 8px;
  padding: 16px 20px;
  margin: 4px 0 8px 64px;
  max-width: 70%;

  @media (max-width: 768px) {
    margin-left: 0;
    max-width: 90%;
  }
`

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`

const CardIcon = styled.div`
  color: ${({ $status }) =>
    $status === 'confirmed' ? '#4caf50' :
    $status === 'failed' ? '#f44336' : '#f57c00'};
  display: flex;
  align-items: center;
`

const CardTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};
  text-transform: capitalize;
`

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 6px 16px;
  margin-bottom: 16px;
  font-size: 13px;
`

const FieldLabel = styled.span`
  color: ${({ theme }) => theme.colors.grey[200]};
  text-transform: capitalize;
  white-space: nowrap;
`

const FieldValue = styled.span`
  color: ${({ theme }) => theme.colors.haiti};
  font-weight: 500;
`

const Actions = styled.div`
  display: flex;
  gap: 8px;
`

const HintText = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[200]};
  margin-top: 8px;
  font-style: italic;
`

const StatusMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  font-size: 14px;
  color: ${({ $success }) => $success ? '#4caf50' : '#f44336'};
`

const HIDDEN_FIELDS = [
  'action', 'confirmationToken', 'message', 'confirmation_token',
  'assigneeId', 'assignee_id', 'userId', 'user_id', 'approverId', 'approver_id',
  'taskId', 'task_id', 'unitId', 'unit_id', 'formTemplateId', 'form_template_id',
  'bpId', 'bp_id', 'projectId', 'project_id', 'teamId', 'team_id',
  'changes', // we flatten this instead
  'metadata', 'requiresApproval', 'requires_approval',
]

const formatFieldName = (key) =>
  key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()

/**
 * Flatten preview fields — expands nested 'changes' object
 * and filters out IDs, keeping only human-readable values
 */
const flattenPreview = (preview) => {
  const result = {}
  for (const [key, value] of Object.entries(preview)) {
    if (key === 'changes' && typeof value === 'object' && value !== null && !Array.isArray(value)) {
      // Flatten changes into top-level fields
      for (const [ck, cv] of Object.entries(value)) {
        result[ck] = cv
      }
    } else {
      result[key] = value
    }
  }
  return result
}

const GuruActionCard = ({ action, onConfirm, onCancel, confirmStatus }) => {
  const [submitting, setSubmitting] = useState(false)
  const [localStatus, setLocalStatus] = useState(null) // 'confirmed' | 'failed' | 'cancelled'
  const [failureMessage, setFailureMessage] = useState(null)
  const [edits, setEdits] = useState({})
  const [fieldErrors, setFieldErrors] = useState({})
  const [incidentDraft, setIncidentDraft] = useState(() =>
    action?.action === 'create_incident' ? initIncidentDraft(action?.preview) : null
  )

  const dispatch = useDispatch()
  const navigate = useNavigate()

  if (!action) return null

  if (action.navigate?.url) {
    const goTo = () => {
      dispatch(closeWidget())
      navigate(action.navigate.url)
    }
    return (
      <Card $status={null}>
        <Actions>
          <Button
            variant="contained"
            size="small"
            startIcon={<OpenInNewIcon fontSize="small" />}
            onClick={goTo}
            sx={{ backgroundColor: '#f57c00', '&:hover': { backgroundColor: '#e65100' }, textTransform: 'none', borderRadius: '6px' }}
          >
            {action.navigate.label || 'Open'}
          </Button>
        </Actions>
      </Card>
    )
  }

  const preview = action.preview || {}
  const actionLabel = action.action === 'link_remedial_to_task'
    ? 'link a remedial to an existing task'
    : (action.action || '').replace(/_/g, ' ')
  const status = confirmStatus || localStatus
  const schemaFields = Array.isArray(preview.fields) ? preview.fields : null
  const isIncident = action.action === 'create_incident'
  const showActions = isIncident ? status !== 'confirmed' : !status

  const valueOf = (field) => (field.key in edits ? edits[field.key] : field.value)
  const onFieldChange = (key, val) => setEdits((prev) => ({ ...prev, [key]: val }))

  const handleConfirm = async () => {
    setSubmitting(true)
    setFieldErrors({})
    try {
      let payloadEdits = Object.keys(edits).length ? edits : null
      if (action.action === 'create_incident' && incidentDraft) {
        payloadEdits = {
          severity: incidentDraft.severity,
          category: incidentDraft.category,
          suggestedActions: cleanRemedials(incidentDraft.suggestedActions),
        }
      }
      const result = await onConfirm(action.confirmationToken, payloadEdits, action)
      if (result?.data && result.data.success === false) {
        throw { response: { data: result.data, status: result.data.status } }
      }
      setLocalStatus('confirmed')
    } catch (err) {
      const data = err?.response?.data
      const apiMessage = data?.error || data?.message
      const httpStatus = err?.response?.status
      if (httpStatus === 404) {
        setFailureMessage('This proposal expired. Ask Guru to suggest it again.')
      } else if (apiMessage) {
        setFailureMessage(apiMessage)
      } else {
        setFailureMessage(null)
      }
      if (data?.errors) setFieldErrors(data.errors)
      setLocalStatus('failed')
    }
    setSubmitting(false)
  }

  const flatFields = flattenPreview(preview)
  const visibleFields = Object.entries(flatFields).filter(
    ([key, value]) => value !== null && value !== undefined && !HIDDEN_FIELDS.includes(key)
  )

  if (status === 'cancelled') return null

  if (schemaFields) {
    if (status === 'confirmed') {
      return (
        <Card $status="confirmed">
          <StatusMessage $success>
            <CheckCircleIcon fontSize="small" />
            {preview.summary || 'Done'}
          </StatusMessage>
        </Card>
      )
    }
    return (
      <Card $status={status}>
        <CardHeader>
          <CardIcon $status={status}><AssignmentIcon /></CardIcon>
          <CardTitle style={{ textTransform: 'none' }}>{preview.title || actionLabel}</CardTitle>
        </CardHeader>
        {preview.summary && <HintText style={{ marginTop: 0, marginBottom: 12, fontStyle: 'normal' }}>{preview.summary}</HintText>}
        {schemaFields.map((field) => (
          <SchemaField
            key={field.key}
            field={field}
            value={valueOf(field)}
            onChange={onFieldChange}
            error={fieldErrors[field.key]}
          />
        ))}
        {status === 'failed' && (
          <StatusMessage style={{ marginTop: 10 }}>
            <ErrorIcon fontSize="small" />
            {failureMessage || 'Something went wrong.'}
          </StatusMessage>
        )}
        <Actions style={{ marginTop: 14 }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleConfirm}
            disabled={submitting}
            sx={{ backgroundColor: '#f57c00', '&:hover': { backgroundColor: '#e65100' }, textTransform: 'none', borderRadius: '6px' }}
          >
            {submitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : (preview.confirmLabel || preview.confirm_label || 'Confirm')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setLocalStatus('cancelled')}
            disabled={submitting}
            sx={{ borderColor: '#e0e0e0', color: '#666', textTransform: 'none', borderRadius: '6px', '&:hover': { borderColor: '#bbb' } }}
          >
            {preview.cancelLabel || preview.cancel_label || 'Cancel'}
          </Button>
        </Actions>
      </Card>
    )
  }

  const getIcon = () => {
    if (status === 'confirmed') return <CheckCircleIcon />
    if (status === 'failed') return <ErrorIcon />
    return <AssignmentIcon />
  }

  const getTitle = () => {
    if (status === 'confirmed') return 'Confirmed'
    if (status === 'failed') return 'Failed'
    return `Proposed: ${actionLabel}`
  }

  return (
    <Card $status={status}>
      <CardHeader>
        <CardIcon $status={status}>{getIcon()}</CardIcon>
        <CardTitle>{getTitle()}</CardTitle>
      </CardHeader>

      {action.action === 'create_incident' ? (
        status === 'confirmed' ? (
          <IncidentPreview preview={draftToPreview(incidentDraft, preview)} />
        ) : (
          <IncidentEditor
            draft={incidentDraft}
            onChange={setIncidentDraft}
            narrative={preview.narrative}
            occurredAt={preview.occurred_at || preview.occurredAt}
          />
        )
      ) : action.action === 'convert_action_to_task' ? (
        <ConvertPreview preview={preview} />
      ) : action.action === 'link_remedial_to_task' ? (
        <LinkPreview preview={preview} />
      ) : visibleFields.length > 0 ? (
        <FieldsGrid>
          {visibleFields.map(([key, value]) => (
            <React.Fragment key={key}>
              <FieldLabel>{formatFieldName(key)}:</FieldLabel>
              <FieldValue>
                {typeof value === 'object' ? JSON.stringify(value) : String(value)}
              </FieldValue>
            </React.Fragment>
          ))}
        </FieldsGrid>
      ) : null}

      {status === 'confirmed' && (
        <StatusMessage $success>
          <CheckCircleIcon fontSize="small" />
          Action completed successfully
        </StatusMessage>
      )}

      {status === 'failed' && (
        <StatusMessage>
          <ErrorIcon fontSize="small" />
          {failureMessage || 'Action failed. The proposal may have expired or is not available for this Building Passport.'}
        </StatusMessage>
      )}

      {showActions && (
        <>
          <Actions>
            <Button
              variant="contained"
              size="small"
              onClick={handleConfirm}
              disabled={submitting}
              sx={{
                backgroundColor: '#f57c00',
                '&:hover': { backgroundColor: '#e65100' },
                textTransform: 'none',
                borderRadius: '6px',
              }}
            >
              {submitting
                ? <CircularProgress size={16} sx={{ color: '#fff' }} />
                : (isIncident ? (status === 'failed' ? 'Try again' : 'Confirm & file') : 'Confirm')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setLocalStatus('cancelled')}
              disabled={submitting}
              sx={{
                borderColor: '#e0e0e0',
                color: '#666',
                textTransform: 'none',
                borderRadius: '6px',
                '&:hover': { borderColor: '#bbb' },
              }}
            >
              Cancel
            </Button>
          </Actions>
          <HintText>
            {action.action === 'create_incident'
              ? 'Edit the severity, category and remedials above before filing — or add your own.'
              : 'Not quite right? Tell Guru what to change instead of confirming.'}
          </HintText>
        </>
      )}
    </Card>
  )
}

export default GuruActionCard

/* ───────────────────────── Specialised previews ───────────────────────── */

const IncidentPreview = ({ preview }) => {
  const sev = SEVERITY_COLOURS[preview.severity] || SEVERITY_COLOURS.medium
  const categoryLabel = INCIDENT_CATEGORY_LABELS[preview.category] || preview.category
  const occurred = formatDate(preview.occurred_at || preview.occurredAt)
  const suggested = preview.suggested_actions || preview.suggestedActions || []

  return (
    <PreviewWrap>
      <PillRow>
        <Chip $bg={sev.bg} $fg={sev.fg}>
          {preview.severity?.toUpperCase()}
        </Chip>
        {categoryLabel && <Chip $bg="#eef2ff" $fg="#3730a3">{categoryLabel}</Chip>}
        {occurred && (
          <MetaPill>
            <EventOutlinedIcon style={{ fontSize: 12 }} /> {occurred}
          </MetaPill>
        )}
      </PillRow>

      {preview.narrative && (
        <Quote>
          <QuoteRule />
          <QuoteText>{preview.narrative}</QuoteText>
        </Quote>
      )}

      {suggested.length > 0 && (
        <SubSection>
          <SubSectionHead>
            <BoltOutlinedIcon style={{ fontSize: 14, color: '#D24724' }} />
            {suggested.length} suggested follow-up remedial{suggested.length === 1 ? '' : 's'}
          </SubSectionHead>
          <SubList>
            {suggested.map((sa, i) => {
              const subSev = SEVERITY_COLOURS[sa.severity] || SEVERITY_COLOURS.medium
              const due = formatDate(sa.suggested_due_by || sa.suggestedDueBy)
              return (
                <SubItem key={i}>
                  <SubItemHead>
                    <SubChip $bg={subSev.bg} $fg={subSev.fg}>{sa.severity}</SubChip>
                    <SubItemTitle>{sa.title}</SubItemTitle>
                  </SubItemHead>
                  {sa.description && <SubItemDesc>{sa.description}</SubItemDesc>}
                  {due && (
                    <SubItemMeta>
                      <EventOutlinedIcon style={{ fontSize: 11 }} /> Suggested due {due}
                    </SubItemMeta>
                  )}
                </SubItem>
              )
            })}
          </SubList>
        </SubSection>
      )}
    </PreviewWrap>
  )
}

const IncidentEditor = ({ draft, onChange, narrative, occurredAt }) => {
  if (!draft) return null
  const occurred = formatDate(occurredAt)
  const severityColour = (sev) => SEVERITY_COLOURS[sev] || SEVERITY_COLOURS.medium

  const setField = (key, val) => onChange((d) => ({ ...d, [key]: val }))
  const updateRemedial = (i, key, val) =>
    onChange((d) => ({
      ...d,
      suggestedActions: d.suggestedActions.map((sa, idx) => (idx === i ? { ...sa, [key]: val } : sa)),
    }))
  const removeRemedial = (i) =>
    onChange((d) => ({ ...d, suggestedActions: d.suggestedActions.filter((_, idx) => idx !== i) }))
  const addRemedial = () =>
    onChange((d) => ({
      ...d,
      suggestedActions: [
        ...d.suggestedActions,
        { title: '', severity: 'medium', description: '', suggestedDueBy: null },
      ],
    }))

  const count = draft.suggestedActions.length

  return (
    <PreviewWrap>
      <EditorTopRow>
        <TextField
          select
          size="small"
          label="Severity"
          value={draft.severity}
          onChange={(e) => setField('severity', e.target.value)}
          sx={{ minWidth: 130, '& .MuiSelect-select': { color: severityColour(draft.severity).fg, fontWeight: 600 } }}
        >
          {SEVERITY_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Category"
          value={draft.category}
          onChange={(e) => setField('category', e.target.value)}
          sx={{ minWidth: 170 }}
        >
          {CATEGORY_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
          ))}
        </TextField>
        {occurred && (
          <MetaPill>
            <EventOutlinedIcon style={{ fontSize: 12 }} /> {occurred}
          </MetaPill>
        )}
      </EditorTopRow>

      {narrative && (
        <Quote>
          <QuoteRule />
          <QuoteText>{narrative}</QuoteText>
        </Quote>
      )}

      <SubSection>
        <SubSectionHead>
          <BoltOutlinedIcon style={{ fontSize: 14, color: '#D24724' }} />
          {count} remedial{count === 1 ? '' : 's'} to file
        </SubSectionHead>

        {count === 0 && <EmptyRemedials>No remedials yet — add one below.</EmptyRemedials>}

        <SubList>
          {draft.suggestedActions.map((sa, i) => (
            <RemedialEditCard key={i}>
              <RemedialEditTop>
                <TextField
                  select
                  size="small"
                  label="Severity"
                  value={sa.severity}
                  onChange={(e) => updateRemedial(i, 'severity', e.target.value)}
                  sx={{ minWidth: 116, '& .MuiSelect-select': { color: severityColour(sa.severity).fg, fontWeight: 600, fontSize: 12 } }}
                >
                  {SEVERITY_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </TextField>
                <IconButton
                  aria-label="Remove remedial"
                  size="small"
                  onClick={() => removeRemedial(i)}
                  sx={{ color: '#94a3b8', '&:hover': { color: '#b00020' } }}
                >
                  <DeleteOutlineIcon style={{ fontSize: 18 }} />
                </IconButton>
              </RemedialEditTop>
              <TextField
                fullWidth
                size="small"
                margin="dense"
                label="What needs doing?"
                value={sa.title}
                onChange={(e) => updateRemedial(i, 'title', e.target.value)}
              />
              <TextField
                fullWidth
                size="small"
                margin="dense"
                label="Detail (optional)"
                multiline
                minRows={1}
                value={sa.description || ''}
                onChange={(e) => updateRemedial(i, 'description', e.target.value)}
              />
              <TextField
                size="small"
                margin="dense"
                label="Due date (optional)"
                type="date"
                value={toDateInput(sa.suggestedDueBy)}
                onChange={(e) => updateRemedial(i, 'suggestedDueBy', e.target.value || null)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 170 }}
              />
            </RemedialEditCard>
          ))}
        </SubList>

        <Button
          type="button"
          startIcon={<AddIcon fontSize="small" />}
          onClick={addRemedial}
          size="small"
          sx={{ alignSelf: 'flex-start', textTransform: 'none', color: '#D24724', fontSize: 12 }}
        >
          Add remedial
        </Button>
      </SubSection>
    </PreviewWrap>
  )
}

const LinkPreview = ({ preview }) => {
  const relation = preview.relation === 'related' ? 'related' : 'handles'
  const copy = RELATION_COPY[relation]
  return (
    <PreviewWrap>
      <KvList>
        <KvRow><KvKey>Remedial</KvKey><KvVal>{preview.action_title || preview.actionTitle}</KvVal></KvRow>
        <KvRow><KvKey>Task</KvKey><KvVal>{preview.task_title || preview.taskTitle}</KvVal></KvRow>
        <KvRow><KvKey>Link</KvKey><KvVal>{copy.label}: {copy.help}</KvVal></KvRow>
      </KvList>
    </PreviewWrap>
  )
}

const ConvertPreview = ({ preview }) => {
  const pri = priorityMeta(preview.priority) || PRIORITY_META.medium
  const sev = SEVERITY_COLOURS[preview.severity] || null
  const deadline = formatDate(preview.deadline)

  return (
    <PreviewWrap>
      <PreviewLead>
        <ReportProblemOutlinedIcon style={{ fontSize: 16, color: '#D24724', flexShrink: 0 }} />
        <PreviewLeadText>
          From remedial: <strong>"{preview.title}"</strong>
        </PreviewLeadText>
      </PreviewLead>

      <PillRow>
        <Chip $bg={pri.bg} $fg={pri.fg}>{pri.label.toUpperCase()} PRIORITY</Chip>
        {sev && (
          <Chip $bg={sev.bg} $fg={sev.fg} title="Original remedial severity">
            From {preview.severity}
          </Chip>
        )}
        {deadline && (
          <MetaPill>
            <EventOutlinedIcon style={{ fontSize: 12 }} /> Due {deadline}
          </MetaPill>
        )}
      </PillRow>

      <KvList>
        {preview.assignee_name && (
          <KvRow><KvKey>Assigned to</KvKey><KvVal>{preview.assignee_name}</KvVal></KvRow>
        )}
        {preview.unit_name && (
          <KvRow><KvKey>Unit</KvKey><KvVal>{preview.unit_name}</KvVal></KvRow>
        )}
        {preview.form_template_name && (
          <KvRow><KvKey>Form template</KvKey><KvVal>{preview.form_template_name}</KvVal></KvRow>
        )}
      </KvList>
    </PreviewWrap>
  )
}

const PreviewWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 14px;
`

const PillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
`

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 3px 10px;
  border-radius: 999px;
  background: ${({ $bg }) => $bg};
  color: ${({ $fg }) => $fg};
`

const MetaPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #475569;
  padding: 3px 10px;
  border-radius: 999px;
  background: #f1f5f9;
`

const Quote = styled.div`
  display: flex;
  gap: 10px;
  background: #fafbfc;
  border: 1px solid #f1f5f9;
  border-radius: 8px;
  padding: 10px 12px;
`

const QuoteRule = styled.span`
  width: 3px;
  border-radius: 2px;
  background: #cbd5e1;
  flex-shrink: 0;
`

const QuoteText = styled.div`
  font-size: 13px;
  color: #0f172a;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
`

const SubSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const SubSectionHead = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #0f172a;
`

const SubList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const SubItem = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 10px;
`

const SubItemHead = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const SubChip = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 2px 7px;
  border-radius: 999px;
  background: ${({ $bg }) => $bg};
  color: ${({ $fg }) => $fg};
  flex-shrink: 0;
`

const SubItemTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.35;
`

const SubItemDesc = styled.div`
  font-size: 11px;
  color: #475569;
  line-height: 1.5;
  margin-top: 4px;
`

const SubItemMeta = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  color: #64748b;
  margin-top: 4px;
`

const EditorTopRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
`

const RemedialEditCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 10px 10px;
`

const RemedialEditTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const EmptyRemedials = styled.div`
  font-size: 12px;
  color: #64748b;
`

const PreviewLead = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const PreviewLeadText = styled.div`
  font-size: 13px;
  color: #0f172a;
  strong { font-weight: 600; }
`

const KvList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const KvRow = styled.div`
  display: grid;
  grid-template-columns: 110px minmax(0, 1fr);
  font-size: 12px;
  gap: 8px;
`

const KvKey = styled.span`
  color: #64748b;
  font-weight: 500;
`

const KvVal = styled.span`
  color: #0f172a;
`
